import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { assistError, assistSessionPath, formatDateTime } from "../../lib/assist";
import { NOTE_FIELDS, reportExcerpt } from "../../lib/assistNotes";
import { hasPermission } from "../../lib/permissions";
import AssistNotesPanel from "./AssistNotesPanel";
import DeleteAssistNote from "./DeleteAssistNote";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { toast } from "sonner";
import { AlertTriangle, ArrowRight, FileText, Loader2, RefreshCw, Search } from "lucide-react";

const SEARCH_DELAY_MS = 300;

// One report, read in a drawer (fresh from the server: the list only holds excerpts). Editing happens on the assistance page,
// open to the technicians of that session and to supervisors — the link is shown only to them.
function NoteSheet({ requestId, user, onClose, onDeleted }) {
  const [state, setState] = useState({ phase: "loading" });
  useEffect(() => {
    if (!requestId) return undefined;
    let alive = true;
    setState({ phase: "loading" });
    api.get(`/assist/requests/${requestId}/notes`)
      .then(({ data }) => alive && setState({ phase: "ready", note: data }))
      .catch((e) => alive && setState({ phase: "error", message: assistError(e).message }));
    return () => { alive = false; };
  }, [requestId]);

  const note = state.note;
  const tookPart = note?.technicians?.some((t) => t.id === user?.id);
  const canOpen = note && (tookPart || hasPermission(user, "support:view_all"));
  const panel = note && {
    phase: "ready", canEdit: false, note,
    fields: Object.fromEntries(NOTE_FIELDS.map(({ key }) => [key, note.fields[key]])),
  };
  return (
    <Sheet open={Boolean(requestId)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="assist-note-sheet">
        <SheetHeader className="pr-8">
          <SheetTitle>{note?.pharmacy || "Compte rendu"}</SheetTitle>
          <SheetDescription>{note ? `Demande du ${formatDateTime(note.requested_at)}` : "Compte rendu d'assistance"}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          {state.phase === "loading" && <div className="space-y-2"><Skeleton className="h-16 w-full" /><Skeleton className="h-24 w-full" /></div>}
          {state.phase === "error" && <p className="text-sm text-slate-600">{state.message}</p>}
          {note && (
            <>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-xs text-slate-500">Motif de la demande</dt>
                  <dd className="whitespace-pre-line break-words text-slate-900">{note.reason || "Aucun motif"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-slate-500">Techniciens</dt>
                  <dd className="text-slate-900">{note.technicians.map((t) => t.name).join(", ") || "Non précisé"}</dd>
                </div>
              </dl>
              <div className="border-t border-slate-100 pt-4"><AssistNotesPanel notes={panel} /></div>
              {canOpen && <div><DeleteAssistNote requestId={note.request_id} onDeleted={() => { onClose(); onDeleted(); }} /></div>}
              {canOpen && (
                <Button asChild variant="outline" size="sm">
                  <Link to={assistSessionPath(note.request_id)} data-testid="assist-note-sheet-open">
                    Ouvrir l'assistance<ArrowRight size={14} className="ml-1.5" />
                  </Link>
                </Button>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// « Comptes rendus » tab: every report, newest first, searchable (pharmacy, reason, technicians, text). Read by every technician
// and supervisor — finding how a colleague solved the same problem is the point.
// `openRequest` ({id}, a new object each time): a report to open in the drawer, from the global search.
export default function AssistNotesList({ user, openRequest = null }) {
  const [query, setQuery] = useState("");
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [openId, setOpenId] = useState(openRequest?.id || null);
  const seq = useRef(0);
  const searched = query.trim();

  const load = useCallback(async (q) => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data } = await api.get("/assist/notes", { params: q ? { q } : {} });
      if (mine !== seq.current) return;
      setItems(data.items);
      setCursor(data.next_cursor || null);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(assistError(e).message);
      setPhase("error");
    }
  }, []);
  useEffect(() => {
    if (searched.length === 1) return undefined;  // the server needs 2 characters: keep the current list meanwhile
    const timer = setTimeout(() => load(searched), searched ? SEARCH_DELAY_MS : 0);
    return () => clearTimeout(timer);
  }, [searched, load]);
  useEffect(() => { if (openRequest) setOpenId(openRequest.id); }, [openRequest]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/assist/notes", { params: { cursor, ...(searched ? { q: searched } : {}) } });
      setItems((prev) => [...prev, ...data.items.filter((i) => !prev.some((p) => p.request_id === i.request_id))]);
      setCursor(data.next_cursor || null);
    } catch (e) {
      toast.error(assistError(e).message);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="assist-notes-tab">
      <div className="relative max-w-md">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <Input type="search" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 bg-white"
          placeholder="Pharmacie, problème, solution, technicien…" aria-label="Rechercher dans les comptes rendus" data-testid="assist-notes-search" />
      </div>

      {phase === "loading" && (
        <div className="space-y-2" data-testid="assist-notes-list-loading"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>
      )}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3" data-testid="assist-notes-list-error">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={() => load(searched)}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="assist-notes-list-empty">
          <FileText className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">{searched ? "Aucun compte rendu ne correspond" : "Aucun compte rendu pour le moment"}</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            {searched ? `Rien pour « ${searched} ». Essayez un autre mot : la pharmacie, le problème, la solution…`
              : "Les notes prises pendant les assistances apparaîtront ici, pour retrouver comment un problème a été réglé."}
          </p>
        </Card>
      )}
      {phase === "ready" && items.length > 0 && (
        <Card className="overflow-hidden" data-testid="assist-notes-list">
          {items.map((item) => {
            const excerpt = reportExcerpt(item);
            return (
              <button key={item.request_id} type="button" onClick={() => setOpenId(item.request_id)} data-testid="assist-notes-row"
                data-request-id={item.request_id}
                className="block w-full px-4 py-3 text-left border-b border-slate-100 last:border-b-0 hover:bg-slate-50 focus-visible:outline-none focus-visible:bg-slate-50">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                  <span className="font-medium text-sm text-slate-900">{item.pharmacy || "Pharmacie non précisée"}</span>
                  <span className="text-xs text-slate-500">{formatDateTime(item.requested_at)}</span>
                </div>
                <p className="text-xs text-slate-500 line-clamp-1">
                  {item.reason || "Aucun motif"}{item.technicians?.length ? ` · ${item.technicians.map((t) => t.name).join(", ")}` : ""}
                </p>
                {excerpt && (
                  <p className="mt-1 text-sm text-slate-700 line-clamp-2 break-words">
                    <span className="text-slate-500">{excerpt.label} : </span>{excerpt.text}
                  </p>
                )}
              </button>
            );
          })}
        </Card>
      )}
      {phase === "ready" && cursor && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="assist-notes-more">
            {loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger plus
          </Button>
        </div>
      )}

      <NoteSheet requestId={openId} user={user} onClose={() => setOpenId(null)} onDeleted={() => load(searched)} />
    </div>
  );
}
